import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router";
import { useLibraryNode } from "../../lib/hooks/use-kuvio";
import { Card, EmptyState, EditableTitle, Breadcrumb, Pill } from "../components/primitives";
import type { BreadcrumbSegment } from "../components/primitives";
import { useUpdatePrompt } from "../hooks/use-prompt-mutations";
import { PromptArgumentsEditor } from "../components/scope/PromptArgumentsEditor";
import { PromptTestCompose } from "../components/scope/PromptTestCompose";
import type { LibraryNode, PromptArgDef, PromptArgType } from "../../lib/types";

type SaveState = "idle" | "saving" | "saved";

const ARG_TYPES: PromptArgType[] = ["text", "id", "date", "number"];

/** Parse a node's frontmatter.arguments into a clean PromptArgDef[] (tolerant). */
function parseArgs(node: LibraryNode | undefined): PromptArgDef[] {
  const raw = (node?.frontmatter as Record<string, unknown> | null)?.arguments;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is Record<string, unknown> => !!a && typeof a === "object")
    .map((a) => ({
      name: typeof a.name === "string" ? a.name : "",
      type: (ARG_TYPES.includes(a.type as PromptArgType) ? a.type : "text") as PromptArgType,
      required: a.required === true,
      ...(typeof a.description === "string" && a.description ? { description: a.description } : {}),
    }))
    .filter((a) => a.name.length > 0);
}

/**
 * Sprint 11 (S11.38–41) — full-page editor for a Prompt node. The DOES analogue
 * of PageView: name + description + a markdown body editor (with a highlighted
 * preview distinguishing {{arg}} / {{?tool}} / [[ref]]), a typed arguments
 * editor, and a live test-compose panel. Rendered by LibraryMemoryPage when the
 * node at /library/:id is a prompt.
 */
export function PromptView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const node = useLibraryNode(id);
  const update = useUpdatePrompt();

  const [bodyDraft, setBodyDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [argsDraft, setArgsDraft] = useState<PromptArgDef[]>([]);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const savedBodyRef = useRef("");
  const savedDescRef = useRef("");
  const savedArgsRef = useRef("");
  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (node.data && loadedIdRef.current !== node.data.id) {
      loadedIdRef.current = node.data.id;
      const body = node.data.document ?? "";
      const desc = node.data.description ?? "";
      const args = parseArgs(node.data);
      setBodyDraft(body);
      setDescDraft(desc);
      setArgsDraft(args);
      savedBodyRef.current = body;
      savedDescRef.current = desc;
      savedArgsRef.current = JSON.stringify(args);
      setSaveState("idle");
    }
  }, [node.data]);

  if (node.isLoading) {
    return (
      <div style={{ maxWidth: 980 }}>
        <div className="kuvio-skeleton" style={{ height: 160, borderRadius: "var(--border-radius-lg)", background: "var(--color-background-secondary)" }} />
      </div>
    );
  }
  if (node.error || !node.data) {
    return (
      <Card>
        <EmptyState>{node.error?.message ?? "Prompt not found"}</EmptyState>
      </Card>
    );
  }

  const prompt = node.data;
  const promptId = prompt.id;

  async function flushSaves() {
    const patch: { prompt_id: string; body?: string; description?: string; arguments?: PromptArgDef[] } = {
      prompt_id: promptId,
    };
    let dirty = false;
    if (bodyDraft !== savedBodyRef.current) { patch.body = bodyDraft; dirty = true; }
    if (descDraft !== savedDescRef.current) { patch.description = descDraft; dirty = true; }
    if (JSON.stringify(argsDraft) !== savedArgsRef.current) { patch.arguments = argsDraft; dirty = true; }
    if (!dirty) return;
    setSaveState("saving");
    try {
      await update.mutateAsync(patch);
      savedBodyRef.current = bodyDraft;
      savedDescRef.current = descDraft;
      savedArgsRef.current = JSON.stringify(argsDraft);
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }

  const segments: BreadcrumbSegment[] = (prompt._breadcrumb ?? [])
    .filter((b) => b.id !== prompt.id)
    .map((b) => ({
      label: b.name,
      onClick: () => (b.type === "agent" ? navigate(`/agents/${b.id}`) : navigate(`/library/${b.id}`)),
    }));
  segments.push({ label: prompt.name });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
      <Breadcrumb segments={segments} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Pill variant="amber" icon="bolt">Prompt</Pill>
        <EditableTitle
          value={prompt.name}
          onSave={async (name) => { await update.mutateAsync({ prompt_id: promptId, name }); }}
        />
        <span style={saveHintStyle}>
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : ""}
        </span>
      </div>

      <label style={fieldStyle}>
        <span style={fieldLabelStyle}>Description</span>
        <input
          style={inputStyle}
          value={descDraft}
          onChange={(e) => { setDescDraft(e.target.value); setSaveState("idle"); }}
          onBlur={() => { void flushSaves(); }}
          placeholder="What invoking this prompt produces, e.g. 'Handle an inbound sales enquiry'."
        />
      </label>

      <Section title="Arguments" hint="Typed inputs the template declares — referenced as {{name}} in the body.">
        <div onBlur={() => { void flushSaves(); }}>
          <PromptArgumentsEditor args={argsDraft} onChange={(a) => { setArgsDraft(a); setSaveState("idle"); }} />
        </div>
      </Section>

      <Section
        title="Template"
        hint="{{arg}} substitutes an argument · {{?tool key=value}} runs a read-only tool · [[node-name]] embeds another node."
      >
        <div style={splitStyle}>
          <div style={paneStyle}>
            <div style={paneLabelStyle}>Body</div>
            <textarea
              value={bodyDraft}
              onChange={(e) => { setBodyDraft(e.target.value); setSaveState("idle"); }}
              onBlur={() => { void flushSaves(); }}
              placeholder="Write the prompt template…  {{enquiry_text}}  {{?list_agent_priorities node_id=ln_…}}  [[sales-playbook]]"
              spellCheck
              style={editorStyle}
            />
          </div>
          <div style={paneStyle}>
            <div style={paneLabelStyle}>Highlighted</div>
            <Card style={previewCardStyle}>
              {bodyDraft.trim() ? <HighlightedTemplate body={bodyDraft} /> : <EmptyState>Nothing yet.</EmptyState>}
            </Card>
          </div>
        </div>
      </Section>

      <Section title="Test compose" hint="Fill the arguments and run the real compose engine against the live workspace.">
        <PromptTestCompose promptName={prompt.name} args={argsDraft} onBeforeCompose={flushSaves} />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Highlighted template preview — S11.41. Distinguishes {{arg}} (blue),
// {{?tool}} (purple), [[ref]] (green) via a single tokenising pass.
// ---------------------------------------------------------------------------

const TOKEN_RE = /(\{\{\?[^}]*\}\}|\{\{[^}]*\}\}|\[\[[^\]]*\]\])/g;

function HighlightedTemplate({ body }: { body: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  let key = 0;
  while ((m = TOKEN_RE.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const tok = m[0];
    const color = tok.startsWith("{{?")
      ? "var(--c-purple-600, #8b5cf6)"
      : tok.startsWith("{{")
        ? "var(--c-blue-600, #2563eb)"
        : "var(--c-green-600, #16a34a)";
    parts.push(
      <span key={key++} style={{ color, fontWeight: 600 }}>
        {tok}
      </span>,
    );
    last = m.index + tok.length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return <pre style={highlightPre}>{parts}</pre>;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{title}</div>
        {hint ? <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>{hint}</div> : null}
      </div>
      {children}
    </div>
  );
}

const saveHintStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  marginLeft: "auto",
  fontVariantNumeric: "tabular-nums",
};
const fieldStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabelStyle: CSSProperties = { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)" };
const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};
const splitStyle: CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" };
const paneStyle: CSSProperties = { flex: "1 1 360px", minWidth: 280, display: "flex", flexDirection: "column", gap: 6 };
const paneLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const editorStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 320,
  padding: "12px 14px",
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--color-text-primary)",
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  outline: "none",
  resize: "vertical",
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
};
const previewCardStyle: CSSProperties = { minHeight: 320, overflowY: "auto" };
const highlightPre: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  color: "var(--color-text-primary)",
};
